<template>
  <div style="text-align:left;">
    <TopNav />
    <div class="column-left">
      <SideNav />
    </div>

    <div class="column-right">
      <ModalPerfilProfesional ref="modalPerfil" :categorias="categorias" />
      <div class="main-container">
        <div class="articulo-principal">
          <h2>Presupuestos pendientes de aprobación</h2>
          <p class="descripcion">
            Revise los presupuestos enviados por los profesionales para continuar con los trabajos solicitados.
            Puede aceptar o rechazar cada propuesta y revisar el detalle de los ítems incluidos.
          </p>

          <div v-if="loading" class="estado">Cargando información...</div>
          <div v-else>
            <div v-if="error" class="estado estado-error">{{ error }}</div>

            <div v-else-if="!presupuestos.length" class="estado estado-empty">
              No hay presupuestos pendientes de aprobación en este momento.
            </div>

            <div v-else>
              <div class="resumen">
                <div>
                  <strong>Total de presupuestos:</strong>
                  {{ presupuestos.length }}
                </div>
              </div>

              <table class="tablex">
                <thead>
                  <tr>
                    <th></th>
                    <th>Presupuesto</th>
                    <th>Solicitud</th>
                    <th>Dirección</th>
                    <th>Profesional</th>
                    <th>Cliente</th>
                    <th>Monto total</th>
                    <th>Enviado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="(presupuesto, index) in presupuestos">
                    <tr
                      :key="`presupuesto-${presupuesto.id}`"
                      :class="{ expanded: expandedIndex === index }"
                    >
                      <td>
                        <button class="btn-detalle" @click="toggleExpand(index)">
                          {{ expandedIndex === index ? 'Ocultar' : 'Ver' }}
                        </button>
                      </td>
                      <td>
                        <div class="texto-principal">#{{ presupuesto.id }}</div>
                        <div v-if="presupuesto.dias_pendiente !== null" class="chip-dias">
                          {{ formatDiasPendiente(presupuesto.dias_pendiente) }}
                        </div>
                      </td>
                      <td>
                        <div class="texto-principal">
                          {{ presupuesto.solicitud_titulo || 'Solicitud sin título' }}
                        </div>
                        <div v-if="presupuesto.solicitud_id" class="texto-secundario">
                          #{{ presupuesto.solicitud_id }}
                        </div>
                      </td>
                      <td>
                        <div class="texto-principal">
                          {{ presupuesto.direccion || 'Sin dirección' }}
                        </div>
                        <div v-if="presupuesto.comuna" class="texto-secundario">
                          {{ presupuesto.comuna }}
                        </div>
                      </td>
                      <td>
                        <template v-if="presupuesto.profesional">
                          <div class="atendido-prof">
                            <img
                              v-if="presupuesto.profesional.foto"
                              :src="presupuesto.profesional.foto"
                              alt="Foto del profesional"
                              class="atendido-avatar"
                            />
                            <img
                              v-else
                              src="@/assets/empty-user.png"
                              alt="Foto del profesional"
                              class="atendido-avatar"
                            />
                            <button
                              class="link-profesional profesional-clickable"
                              @click="openProfesionalModal(presupuesto.profesional.nombre_seo)"
                            >
                              {{ formatProfesionalNombre(presupuesto.profesional) }}
                            </button>
                          </div>
                        </template>
                        <span v-else>Sin asignar</span>
                      </td>
                      <td>
                        <div class="texto-principal">
                          {{ presupuesto.cliente_nombre || 'Cliente no registrado' }}
                        </div>
                      </td>
                      <td>{{ formatCurrency(presupuesto.monto_total) }}</td>
                      <td>{{ formatFecha(presupuesto.fecha_enviado || presupuesto.fecha_creado) }}</td>
                      <td class="acciones-col">
                        <button
                          class="btn-accion btn-download"
                          :disabled="downloadingId === presupuesto.id"
                          @click="descargarPresupuesto(presupuesto)"
                        >
                          {{ downloadingId === presupuesto.id ? 'Descargando…' : 'Descargar' }}
                        </button>
                        <button class="btn-accion btn-outline" @click="openModal(presupuesto, 'reject')">
                          Rechazar
                        </button>
                        <button class="btn-accion btn-primary" @click="openModal(presupuesto, 'accept')">
                          Aceptar
                        </button>
                      </td>
                    </tr>
                    <tr
                      v-if="expandedIndex === index"
                      :key="`presupuesto-detalle-${presupuesto.id}`"
                      class="detalle"
                    >
                      <td colspan="9">
                        <div class="detalle-contenido">
                          <div class="detalle-col">
                            <h4>Ítems del presupuesto</h4>
                            <div v-if="hasItems(presupuesto)" class="table-wrapper">
                              <table class="subtabla">
                                <thead>
                                  <tr>
                                    <th>Ítem</th>
                                    <th>Cantidad</th>
                                    <th>Monto</th>
                                    <th>Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr
                                    v-for="item in presupuesto.desgloce.items || []"
                                    :key="`${presupuesto.id}-${item.id || item.nombre}`"
                                  >
                                    <td>{{ item.nombre || 'Ítem sin nombre' }}</td>
                                    <td>{{ item.cantidad || 1 }}</td>
                                    <td>{{ formatCurrency(item.precio || 0) }}</td>
                                    <td>{{ formatCurrency((item.precio || 0) * (item.cantidad || 1)) }}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                            <div v-else class="sin-datos">Este presupuesto no tiene ítems detallados.</div>
                          </div>
                          <div class="detalle-col notas" v-if="presupuesto.desgloce && presupuesto.desgloce.notas_adicionales">
                            <h4>Notas adicionales</h4>
                            <p>{{ presupuesto.desgloce.notas_adicionales }}</p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showModal"
      class="budget-modal-overlay"
      role="presentation"
      @click="onOverlayClick"
    >
      <div
        ref="modalDialog"
        class="budget-modal-dialog"
        role="dialog"
        aria-modal="true"
        tabindex="-1"
      >
        <header class="modal-header">
          <h3>{{ modalTitle }}</h3>
          <button class="modal-close" @click="closeModal" aria-label="Cerrar">
            <span class="material-icons">close</span>
          </button>
        </header>
        <div class="modal-body">
          <p v-html="modalDescription"></p>
          <div v-if="modalAction === 'accept'" class="modal-items">
            <div v-if="modalHasItems">
              <div class="modal-items-header">
                <label class="modal-select-all">
                  <input type="checkbox" v-model="modalAllSelected" />
                  Aprobar todos los ítems
                </label>
                <span class="modal-selection-count">
                  {{ modalSelectedItemCount }} / {{ modalItems.length }} seleccionados
                </span>
              </div>
              <div class="modal-items-helper">
                Selecciona los ítems del presupuesto que deseas aprobar.
                <span class="modal-selection-total" v-if="modalHasItems">
                  Total seleccionado: {{ formatCurrency(modalSelectedAmount) }}
                </span>
              </div>
              <div class="modal-items-list">
                <div v-for="item in modalItems" :key="item.key" class="modal-item">
                  <div class="modal-item-info">
                    <label class="modal-item-checkbox">
                      <input type="checkbox" v-model="item.selected" @change="modalError = ''" />
                      <span class="modal-item-name">{{ item.nombre }}</span>
                    </label>
                    <div class="modal-item-meta">
                      <span class="modal-item-quantity">Cantidad: {{ item.cantidad }}</span>
                      <span class="modal-item-price">Unitario: {{ formatCurrency(item.precio) }}</span>
                    </div>
                  </div>
                  <div class="modal-item-total">{{ formatCurrency(item.precio * item.cantidad) }}</div>
                </div>
              </div>
            </div>
            <div v-else class="modal-items-empty">
              Este presupuesto no tiene ítems para aprobar.
            </div>
          </div>
          <div v-if="modalAction === 'reject'" class="modal-field">
            <label for="modal-comment">Motivo (opcional)</label>
            <textarea
              id="modal-comment"
              v-model="modalComment"
              rows="3"
              placeholder="Explique brevemente el motivo del rechazo"
            ></textarea>
          </div>
          <div v-if="modalError" class="modal-error">{{ modalError }}</div>
        </div>
        <footer class="modal-footer">
          <button
            type="button"
            class="btn-accion btn-outline"
            :disabled="processingAction"
            @click="closeModal"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="btn-accion"
            :class="modalAction === 'accept' ? 'btn-primary' : 'btn-danger'"
            :disabled="processingAction"
            @click="confirmAction"
          >
            {{ processingAction ? 'Procesando...' : primaryActionLabel }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters, mapActions } from 'vuex';
import TopNav from '@/components/TopNav.vue';
import SideNav from '@/components/SideNav.vue';
import ModalPerfilProfesional from '@/components/ModalPerfilProfesional.vue';

export default {
  name: 'PresupuestosPendientes',
  components: {
    TopNav,
    SideNav,
    ModalPerfilProfesional,
  },
  data() {
    return {
      presupuestos: [],
      loading: true,
      error: null,
      expandedIndex: null,
      showModal: false,
      modalAction: null,
      modalComment: '',
      processingAction: false,
      selectedPresupuesto: null,
      removeModalKeydown: null,
      downloadingId: null,
      modalItems: [],
      modalError: '',
    };
  },
  computed: {
    ...mapGetters({
      categorias: 'categorias',
      admin_datos: 'admin_datos',
    }),
    modalTitle() {
      if (!this.selectedPresupuesto) {
        return 'Confirmar acción';
      }
      return this.modalAction === 'accept' ? 'Aceptar presupuesto' : 'Rechazar presupuesto';
    },
    primaryActionLabel() {
      return this.modalAction === 'accept' ? 'Aceptar' : 'Rechazar';
    },
    modalDescription() {
      if (!this.selectedPresupuesto) {
        return '';
      }
      const profesional = this.selectedPresupuesto.profesional;
      const nombreProfesional = profesional ? this.formatProfesionalNombre(profesional) : 'el profesional asignado';
      const monto = this.formatCurrency(this.selectedPresupuesto.monto_total);
      if (this.modalAction === 'accept') {
        return `¿Deseas <strong>aceptar</strong> el presupuesto de <strong>${nombreProfesional}</strong> por <strong>${monto}</strong>?`;
      }
      return `¿Deseas <strong>rechazar</strong> el presupuesto de <strong>${nombreProfesional}</strong> por <strong>${monto}</strong>?`;
    },
    modalHasItems() {
      return Array.isArray(this.modalItems) && this.modalItems.length > 0;
    },
    modalAllSelected: {
      get() {
        if (!this.modalHasItems) {
          return false;
        }
        return this.modalItems.every((item) => item.selected);
      },
      set(value) {
        if (!Array.isArray(this.modalItems)) {
          this.modalItems = [];
          return;
        }
        const shouldSelect = Boolean(value);
        this.modalItems = this.modalItems.map((item) => ({
          ...item,
          selected: shouldSelect,
        }));
        this.modalError = '';
      },
    },
    modalSelectedItemCount() {
      if (!this.modalHasItems) {
        return 0;
      }
      return this.modalItems.filter((item) => item.selected).length;
    },
    modalSelectedAmount() {
      if (!this.modalHasItems) {
        return 0;
      }
      return this.modalItems.reduce((sum, item) => {
        if (!item.selected) {
          return sum;
        }
        const precio = Number(item.precio) || 0;
        const cantidad = Number(item.cantidad) || 0;
        return sum + precio * cantidad;
      }, 0);
    },
  },
  created() {
    this.fetchPropertyManagerPendingBudgetsCount().catch(() => {});
    this.fetchPresupuestos();
  },
  mounted() {
    if (!this.admin_datos || this.admin_datos.user_type !== 'property_manager') {
      this.$router.push('/');
    }
  },
  watch: {
    showModal(value) {
      if (value) {
        this.lockBodyScroll(true);
        this.$nextTick(() => {
          this.focusModal();
          if (!this.removeModalKeydown) {
            const handler = (event) => {
              if (event.key === 'Escape') {
                this.handleEscape();
              }
            };
            document.addEventListener('keydown', handler);
            this.removeModalKeydown = () => {
              document.removeEventListener('keydown', handler);
              this.removeModalKeydown = null;
            };
          }
        });
      } else {
        this.lockBodyScroll(false);
        if (typeof this.removeModalKeydown === 'function') {
          this.removeModalKeydown();
        }
      }
    },
  },
  methods: {
    ...mapActions([
      'fetchPropertyManagerPendingBudgetsCount',
      'updatePropertyManagerPendingBudgetsFromList',
    ]),
    onOverlayClick(event) {
      if (!this.$refs.modalDialog) {
        this.closeModal();
        return;
      }
      const dialogEl = this.$refs.modalDialog;
      const clickedInsideDialog = dialogEl === event.target || dialogEl.contains(event.target);
      if (!clickedInsideDialog) {
        this.closeModal();
      }
    },
    handleEscape() {
      this.closeModal();
    },
    focusModal() {
      this.$refs.modalDialog?.focus?.();
    },
    lockBodyScroll(shouldLock) {
      const body = document.body;
      if (!body) {
        return;
      }
      if (shouldLock) {
        body.classList.add('modal-open');
      } else {
        body.classList.remove('modal-open');
      }
    },
    fetchPresupuestos() {
      this.loading = true;
      this.error = null;
      this.expandedIndex = null;
      axios
        .get(`${window.hostname}api/property_manager/presupuestos_pendientes`)
        .then((response) => {
          const presupuestos = response?.data?.presupuestos || [];
          this.presupuestos = presupuestos.map((p) => {
            const desgloce = p && typeof p.desgloce === 'object' ? p.desgloce : {};
            return {
              ...p,
              desgloce: {
                ...desgloce,
                items: Array.isArray(desgloce.items) ? desgloce.items : [],
              },
            };
          });
          this.handleListChanged();
        })
        .catch((err) => {
          console.error('Error al cargar presupuestos pendientes:', err);
          this.error = 'No fue posible cargar los presupuestos pendientes. Intente nuevamente en unos minutos.';
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handleListChanged() {
      this.updatePropertyManagerPendingBudgetsFromList(this.presupuestos);
    },
    toggleExpand(index) {
      this.expandedIndex = this.expandedIndex === index ? null : index;
    },
    hasItems(presupuesto) {
      return (
        presupuesto &&
        presupuesto.desgloce &&
        Array.isArray(presupuesto.desgloce.items) &&
        presupuesto.desgloce.items.length > 0
      );
    },
    formatCurrency(value) {
      const numberValue = Number(value) || 0;
      return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        minimumFractionDigits: 0,
      }).format(numberValue);
    },
    formatFecha(isoDate) {
      if (!isoDate) {
        return 'Sin fecha';
      }
      try {
        const date = new Date(isoDate);
        if (Number.isNaN(date.getTime())) {
          return 'Sin fecha';
        }
        return date.toLocaleDateString('es-CL', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      } catch (e) {
        return 'Sin fecha';
      }
    },
    formatDiasPendiente(dias) {
      const parsed = Number(dias);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        return 'Pendiente hoy';
      }
      if (parsed === 1) {
        return 'Hace 1 día';
      }
      return `Hace ${parsed} días`;
    },
    formatProfesionalNombre(profesional) {
      if (!profesional) {
        return 'Profesional sin nombre';
      }
      const partes = [profesional.nombre, profesional.apellido].filter(Boolean);
      return partes.length ? partes.join(' ') : 'Profesional sin nombre';
    },
    openProfesionalModal(nombreSeo) {
      if (!nombreSeo || !this.$refs.modalPerfil) {
        return;
      }
      this.$refs.modalPerfil.openByNombreSeo(nombreSeo);
    },
    async descargarPresupuesto(presupuesto) {
      if (!presupuesto || !presupuesto.id || this.downloadingId === presupuesto.id) {
        return;
      }

      const payload = {
        presupuesto_id: presupuesto.id,
        orden_id: presupuesto.orden_id,
        nombre: presupuesto.nombre,
        notas_adicionales: presupuesto.desgloce?.notas_adicionales,
        items: Array.isArray(presupuesto.desgloce?.items) ? presupuesto.desgloce.items : [],
      };

      this.downloadingId = presupuesto.id;
      try {
        const response = await axios.post(
          `${window.hostname}api/property_manager/descargar_presupuesto`,
          payload,
          { responseType: 'blob' },
        );

        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const sanitizedName = `${(presupuesto.nombre || 'presupuesto').replace(/\s+/g, '_')}_${presupuesto.id}.pdf`;
        link.href = url;
        link.setAttribute('download', sanitizedName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error al descargar el presupuesto:', error);
        this.$notify?.({
          group: 'foo',
          title: 'Error',
          text: 'No se pudo descargar el presupuesto. Intente nuevamente.',
          type: 'error',
        });
      } finally {
        this.downloadingId = null;
      }
    },
    openModal(presupuesto, action) {
      if (this.processingAction) {
        return;
      }
      this.selectedPresupuesto = presupuesto;
      this.modalAction = action;
      this.modalComment = '';
      this.modalError = '';
      if (action === 'accept') {
        const items = Array.isArray(presupuesto?.desgloce?.items)
          ? presupuesto.desgloce.items
          : [];
        this.modalItems = items.map((item, index) => {
          const cantidadValue = Number(item?.cantidad);
          const precioValue = Number(item?.precio);
          return {
            key: `${presupuesto.id}-${item?.id ?? index}`,
            id: item?.id,
            nombre: item?.nombre || 'Ítem sin nombre',
            cantidad: Number.isFinite(cantidadValue) && cantidadValue > 0 ? cantidadValue : 1,
            precio: Number.isFinite(precioValue) ? precioValue : 0,
            selected: true,
          };
        });
      } else {
        this.modalItems = [];
      }
      this.showModal = true;
    },
    closeModal() {
      if (this.processingAction) {
        return;
      }
      this.showModal = false;
      this.modalAction = null;
      this.selectedPresupuesto = null;
      this.modalComment = '';
      this.modalItems = [];
      this.modalError = '';
    },
    confirmAction() {
      if (!this.selectedPresupuesto || this.processingAction) {
        return;
      }
      if (this.modalAction === 'accept') {
        if (!this.modalHasItems) {
          this.modalError = 'Este presupuesto no tiene ítems disponibles para aprobar.';
          return;
        }
        if (this.modalSelectedItemCount === 0) {
          this.modalError = 'Selecciona al menos un ítem para aprobar.';
          return;
        }
        this.modalError = '';
        this.acceptPresupuesto();
      } else {
        this.rejectPresupuesto();
      }
    },
    async acceptPresupuesto() {
      if (!this.selectedPresupuesto) {
        return;
      }
      this.processingAction = true;
      try {
        const selectedItems = this.modalItems
          .filter((item) => item.selected)
          .map((item) => item.id)
          .filter((id) => id !== undefined && id !== null);

        if (!selectedItems.length) {
          this.modalError = 'Los ítems seleccionados no están disponibles para su aprobación.';
          this.processingAction = false;
          return;
        }

        await axios.post(`${window.hostname}api/property_manager/aprobar_presupuesto`, {
          presupuesto_id: this.selectedPresupuesto.id,
          selected_items: selectedItems,
        });
        this.presupuestos = this.presupuestos.filter((p) => p.id !== this.selectedPresupuesto.id);
        this.expandedIndex = null;
        this.handleListChanged();
        this.$notify?.({
          group: 'foo',
          title: 'Presupuesto aceptado',
          text: 'El presupuesto se aceptó correctamente.',
          type: 'success',
        });
        this.processingAction = false;
        this.closeModal();
      } catch (error) {
        console.error('Error al aceptar presupuesto:', error);
        const backendError = error?.response?.data?.error;
        if (backendError) {
          this.modalError = backendError;
        } else {
          this.$notify?.({
            group: 'foo',
            title: 'Error',
            text: 'No se pudo aceptar el presupuesto. Intente nuevamente.',
            type: 'error',
          });
        }
      } finally {
        this.processingAction = false;
      }
    },
    async rejectPresupuesto() {
      if (!this.selectedPresupuesto) {
        return;
      }
      this.processingAction = true;
      try {
        await axios.post(`${window.hostname}api/property_manager/rechazar_presupuesto`, {
          presupuesto_id: this.selectedPresupuesto.id,
          comment: this.modalComment,
        });
        this.presupuestos = this.presupuestos.filter((p) => p.id !== this.selectedPresupuesto.id);
        this.expandedIndex = null;
        this.handleListChanged();
        this.$notify?.({
          group: 'foo',
          title: 'Presupuesto rechazado',
          text: 'El presupuesto se rechazó correctamente.',
          type: 'success',
        });
        this.processingAction = false;
        this.closeModal();
      } catch (error) {
        console.error('Error al rechazar presupuesto:', error);
        this.$notify?.({
          group: 'foo',
          title: 'Error',
          text: 'No se pudo rechazar el presupuesto. Intente nuevamente.',
          type: 'error',
        });
      } finally {
        this.processingAction = false;
      }
    },
  },
  beforeDestroy() {
    this.lockBodyScroll(false);
    if (typeof this.removeModalKeydown === 'function') {
      this.removeModalKeydown();
    }
  },
};
</script>

<style scoped>
.column-right {
  background-color: #f2f7fb;
  padding-top: 80px;
  padding-left: 260px;
  width: 100%;
  min-height: 100vh;
  box-sizing: border-box;
}

.main-container {
  background-color: #fff;
  margin: 20px;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.articulo-principal {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.articulo-principal h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
}

.descripcion {
  margin-bottom: 15px;
  color: #555;
  line-height: 1.5;
}

.estado {
  padding: 12px;
  background: #f5f5f5;
  border-radius: 6px;
  margin-top: 10px;
}

.estado-error {
  background: #ffefef;
  color: #c0392b;
}

.estado-empty {
  text-align: center;
  color: #777;
}

.resumen {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 15px;
  flex-wrap: wrap;
}

.tablex {
  width: 100%;
  border-collapse: collapse;
}

.tablex th,
.tablex td {
  padding: 10px;
  border-bottom: 1px solid #e0e0e0;
  text-align: left;
}

.tablex td {
  vertical-align: middle;
}

.tablex thead {
  background: #fafafa;
}

.tablex tr.expanded {
  background: #f9f9f9;
}

.btn-detalle {
  border: none;
  background: transparent;
  color: #007bff;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0;
}

.btn-detalle:hover {
  text-decoration: underline;
}

.atendido-prof {
  display: flex;
  align-items: center;
  gap: 10px;
}

.atendido-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid #e0e0e0;
}

.texto-principal {
  font-weight: 600;
  color: #2c3e50;
}

.texto-secundario {
  color: #7f8c8d;
  font-size: 12px;
  margin-top: 4px;
}

.link-profesional {
  background: none;
  border: none;
  color: #007bff;
  cursor: pointer;
  font-weight: 600;
  padding: 0;
}

.link-profesional:hover {
  text-decoration: underline;
}

.chip-dias {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f0f1ff;
  color: #4b56c0;
  font-size: 12px;
  margin-top: 6px;
}

.acciones-col {
  text-align: right;
  white-space: nowrap;
}

.acciones-col .btn-accion + .btn-accion {
  margin-left: 8px;
}

.btn-accion {
  border: none;
  border-radius: 6px;
  padding: 6px 14px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn-accion:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.btn-outline {
  background: #ffffff;
  border: 1px solid #d0d7e2;
  color: #3b4a67;
}

.btn-outline:hover:not(:disabled) {
  background: #f0f4ff;
}

.btn-download {
  background: #eef3ff;
  border: 1px solid #d0d7e2;
  color: #2c3e50;
}

.btn-download:hover:not(:disabled) {
  background: #dbe4ff;
}

.btn-primary {
  background: #5560f2;
  color: #ffffff;
}

.btn-primary:hover:not(:disabled) {
  background: #3e46c9;
}

.btn-danger {
  background: #dc3545;
  color: #ffffff;
}

.btn-danger:hover:not(:disabled) {
  background: #b02a37;
}

.detalle {
  background: #ffffff;
}

.detalle-contenido {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  padding: 16px 0;
}

.detalle-col {
  flex: 1 1 260px;
}

.detalle-col h4 {
  margin: 0 0 12px;
  font-size: 16px;
}

.table-wrapper {
  overflow-x: auto;
}

.subtabla {
  width: 100%;
  border-collapse: collapse;
  background: #ffffff;
}

.subtabla th,
.subtabla td {
  padding: 8px;
  border-bottom: 1px solid #eeeeee;
  text-align: left;
}

.subtabla thead {
  background: #f1f1f1;
}

.sin-datos {
  color: #777777;
}

.notas p {
  margin: 0;
  white-space: pre-line;
}

.budget-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  z-index: 12000;
}

.budget-modal-dialog {
  background-color: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 24px 48px rgba(15, 30, 84, 0.25);
  display: flex;
  flex-direction: column;
  outline: none;
}

.budget-modal-dialog .modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid rgba(98, 114, 143, 0.15);
}

.budget-modal-dialog .modal-header h3 {
  margin: 0;
  font-size: 20px;
  color: #1f2a3d;
}

.budget-modal-dialog .modal-close {
  border: none;
  background: none;
  cursor: pointer;
  color: #6d7a91;
  display: flex;
  align-items: center;
}

.budget-modal-dialog .modal-body {
  padding: 20px;
  color: #4f5d75;
  line-height: 1.5;
}

.modal-items {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.modal-items-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  color: #2c3e50;
}

.modal-select-all {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #2c3e50;
}

.modal-selection-count {
  font-size: 12px;
  color: #5560f2;
}

.modal-items-helper {
  font-size: 13px;
  color: #6d7a91;
  margin-bottom: 12px;
}

.modal-selection-total {
  display: inline-block;
  margin-left: 8px;
  font-weight: 600;
  color: #1f2a3d;
}

.modal-items-list {
  border: 1px solid #d0d7e2;
  border-radius: 12px;
  overflow: hidden;
  background: #f7f9ff;
  max-height: 240px;
  overflow-y: auto;
}

.modal-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 16px;
  border-bottom: 1px solid #e3e8f5;
}

.modal-item:last-child {
  border-bottom: none;
}

.modal-item-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.modal-item-checkbox {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  color: #2c3e50;
}

.modal-item-name {
  color: #2c3e50;
}

.modal-item-meta {
  display: flex;
  gap: 16px;
  font-size: 12px;
  color: #7f8c8d;
}

.modal-item-total {
  font-weight: 600;
  color: #1f2a3d;
  font-size: 14px;
  min-width: 100px;
  text-align: right;
}

.modal-item-price {
  font-weight: 500;
}

.modal-items-empty {
  padding: 18px;
  border-radius: 12px;
  border: 1px dashed #d0d7e2;
  background: #fbfcff;
  color: #6d7a91;
  font-size: 14px;
  text-align: center;
}

.modal-error {
  margin-top: 14px;
  color: #c0392b;
  font-size: 14px;
}

.budget-modal-dialog .modal-field {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.budget-modal-dialog .modal-field label {
  font-size: 13px;
  font-weight: 600;
  color: #5f7087;
}

.budget-modal-dialog .modal-field textarea {
  width: 100%;
  border-radius: 10px;
  border: 1px solid rgba(98, 114, 143, 0.3);
  padding: 10px;
  resize: vertical;
  font-family: inherit;
}

.budget-modal-dialog .modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px 20px;
  border-top: 1px solid rgba(98, 114, 143, 0.15);
}

.material-icons {
  font-size: 20px;
}

@media (max-width: 1024px) {
  .column-right {
    padding-left: 0;
  }
  .main-container {
    margin: 20px 10px;
  }
}

@media (max-width: 768px) {
  .tablex {
    display: block;
    overflow-x: auto;
  }
  .acciones-col {
    justify-content: flex-start;
    flex-wrap: wrap;
  }
  .detalle-contenido {
    flex-direction: column;
  }
}

:global(body.modal-open) {
  overflow: hidden;
}
</style>
